import React from 'react';
import './clear-all-filters.scss';
import { Button, ButtonGroup } from '@mui/material';
import resetIcon from '@assets/icons/carbon-close-filled.svg';
import {
  BackIcon,
  InputSearchIcon,
  FilterIcon,
  ListViewIcon
} from '@components/icons/svg-icon.tsx';
import { ClearAllFiltersProps } from '@models/table.model.ts';
import { useTranslation } from 'react-i18next';

const ClearAllFilters: React.FC<ClearAllFiltersProps> = ({
  orderedColumns,
  search,
  filters,
  sort,
  betweenDateRangeFilters,
  clearSearch,
  clearSort,
  clearFilters,
  clearDateRangeFilters,
  clearAllFilters,
  extraFilterButtons,
  hiddenFilters
}) => {
  const { t } = useTranslation();

  const toTitleCase = (text: string) =>
    text.charAt(0).toUpperCase() + text.slice(1);

  const getLabel = (columnId: string) => {
    const stripLangSuffix = (id: string) => {
      const parts = id.split('.');
      return parts.length > 1 ? parts.slice(0, -1).join('.') : id;
    };

    const baseId = stripLangSuffix(columnId);

    const matchedColumn =
      orderedColumns.find((col) =>
        col.isTranslated ? col.id === baseId : col.id === columnId
      ) ?? orderedColumns.find((col) => col.field === columnId);

    const label =
      matchedColumn?.label || (matchedColumn?.isTranslated ? baseId : columnId);

    return toTitleCase(t(label));
  };

  const getSortOrderLabel = (order: string) => {
    if (order === 'asc') return 'Ascending';
    if (order === 'desc') return 'Descending';
    return order;
  };

  const renderSearchFilters = () =>
    Object.entries(search)
      .filter(([_, value]) => value.trim() !== '')
      .map(([column, value]) => (
        <Button
          key={`search-${column}`}
          disableRipple
          className="flex items-center gap-2"
          onClick={() => clearSearch?.(column)}
        >
          <InputSearchIcon fill="var(--blue-4)" className="w-4.5! h-4.5!" />{' '}
          <span className="font-semibold! text-[14px] text-(--gray-1)">
            {getLabel(column)}: <span className="font-normal">{value}</span>
          </span>
          <span>
            <img src={resetIcon} alt="Reset Icon" />
          </span>
        </Button>
      ));

  const renderSortFilter = () => {
    const [column, order] = sort;
    if (!column || !order) return null;

    return (
      <Button
        key={`sort-${column}`}
        disableRipple
        className="flex items-center gap-2"
        onClick={clearSort}
      >
        <BackIcon className="rotate-90! w-5! h-5!" fill={'var(--blue-4)'} />
        <span className="font-semibold! text-[14px] text-(--gray-1)">
          {getLabel(column)}:{' '}
          <span className="font-normal">{getSortOrderLabel(order)}</span>
        </span>
        <span>
          <img src={resetIcon} alt="Reset Icon" />
        </span>
      </Button>
    );
  };

  const renderValueFilters = () =>
    Object.entries(filters)
      .filter(([column, values]) => {
        if (hiddenFilters?.includes(column)) return false;
        return values.length > 0;
      })
      .map(([column, values]) => (
        <Button
          key={`filter-${column}`}
          disableRipple
          className="flex items-center gap-2"
          onClick={() => clearFilters?.(column)}
        >
          <span className="flex items-center gap-2">
            <FilterIcon className="w-4.5! h-4.5!" />{' '}
            <span className="font-semibold! text-[14px] text-(--gray-1)">
              {getLabel(column)}:{' '}
              <span className="font-normal">
                {values.map((v) => {
                  const col = orderedColumns.find((c) => c.id === column || c.field === column);
                  const opt = col?.filterData?.find((item: any) => String(item.value) === String(v));
                  return opt?.title ?? v;
                }).join(', ')}
              </span>
            </span>
          </span>
          <span>
            <img src={resetIcon} alt="Reset Icon" />
          </span>
        </Button>
      ));

  const renderDateRangeFilters = () =>
    Object.entries(betweenDateRangeFilters)
      .filter(([_, range]) => range.from && range.to)
      .map(([column, range]) => (
        <Button
          key={`date-${column}`}
          disableRipple
          className="flex items-center gap-2"
          onClick={() => clearDateRangeFilters?.(column)}
        >
          <FilterIcon className="w-4.5! h-4.5!" />
          <span className="font-semibold! text-[14px] text-(--gray-1)">
            {getLabel(column)}:{' '}
            <span className="font-normal">
              {new Date(range.from).toLocaleDateString()} -{' '}
              {new Date(range.to).toLocaleDateString()}
            </span>
          </span>
          <span>
            <img src={resetIcon} alt="Reset Icon" />
          </span>
        </Button>
      ));

  const hasFilters =
    Object.values(search).some((v) => v.trim() !== '') ||
    sort[0] !== '' ||
    Object.entries(filters).some(
      ([key, val]) =>
        !hiddenFilters?.includes(key) &&
        val.length > 0
    ) ||
    Object.values(betweenDateRangeFilters).some((v) => v.from && v.to);

  if (!hasFilters && !extraFilterButtons) return null;

  return (
    <div className="w-full flex justify-between gap-5 items-center pb-4">
      <div className="scrollbar-zero w-[calc(100%-156px)] overflow-x-auto flex items-center gap-3">
        {hasFilters && (
          <ButtonGroup
            variant="outlined"
            aria-label="Active filters"
            sx={{
              '& .MuiButton-root': {
                maxWidth: '100%',
                borderColor: 'var(--gray-6)',
                borderWidth: '1px',
                px: '14px',
                fontSize: '14px',
                fontWeight: 500,
                paddingTop: '4px',
                paddingBottom: '4px',
                textTransform: 'none',
                textWrap: 'nowrap'
              },
              '& .MuiButton-root:first-of-type': {
                borderRadius: '8px 0 0 8px',
                paddingLeft: '16px',
                paddingRight: '16px',
                '&:hover': {
                  backgroundColor: 'inherit'
                }
              },
              '& .MuiButton-root:last-of-type': {
                borderRadius: '0 8px 8px 0'
              }
            }}
          >
            <Button disableRipple>
              <ListViewIcon />
            </Button>
            {renderSearchFilters()}
            {renderSortFilter()}
            {renderValueFilters()}
            {renderDateRangeFilters()}
          </ButtonGroup>
        )}
        {extraFilterButtons}
      </div>

      {hasFilters && (
        <Button
          onClick={clearAllFilters}
          variant="outlined"
          className="text-[14px] px-2! min-w-37.5! text-nowrap text-(--gray-1) font-medium cursor-pointer rounded-lg! border! border-solid! py-[4.9px]! border-(--gray-6)! bg-(--white)"
        >
          Clear All Filters
        </Button>
      )}
    </div>
  );
};

export default ClearAllFilters;
